// İSG Portal — Profil (Self) API çağrıları + tipleri.
//
// KAYNAK: api-contract.md §18 (Profil — Self; `/me` + `/supplier/me`). Backend
// kanonu; burada **varsayım YOK** — endpoint yolları, DTO alanları ve zarf kuralı
// (Result unwrap `value`; camelCase §1) doğrudan sözleşmeden türetilmiştir.
//
// **Scope izolasyonu (§18, kritik).** Kimlik ve aktif firma daima token principal'dan
// okunur — istemci hiçbir yerde `userUuid`/`companyId` göndermez. `/me` yalnız **user**,
// `/supplier/me` yalnız **supplier** principal'a anlamlıdır (yanlış principal → 403
// FORBIDDEN); bu yüzden her app kendi principal ucunu çağırır (expert `/me`, supplier
// `/supplier/me`). Yeni yetki modülü `profile` AllAuthorized'dır (kimlik-doğrulanmış her
// principal grant GEREKMEDEN erişir; kimlik yoksa 401).

import { api } from './client';
import type { Empty } from './envelope';

/**
 * User self-profil — api-contract §18.1 (`GET /me` → `Result<MeDto>`). `uuid` kullanıcının
 * public kimliği; `firstName`/`lastName`/`email` merkezî identity store'dan; `companyUuid`/
 * `companyName` token firmasından (firma silinmiş/çözülemezse **güvenle boş** kalır — 500
 * üretilmez). `roleName` opsiyonel (rolsüz → `null`; çok-rolde adlar `", "` ile birleşir).
 */
export interface MeDto {
  uuid: string;
  firstName: string;
  lastName: string;
  email: string;
  companyUuid: string | null;
  companyName: string | null;
  roleName?: string | null;
}

/**
 * Supplier self-profil — api-contract §18.2 (`GET /supplier/me` → `Result<SupplierMeDto>`).
 * `uuid` taşeron personelinin public kimliği; `emailOrPhone` giriş/iletişim hedefi (e-posta
 * öncelikli, yoksa telefon); `companyUuid`/`companyName` token'daki **aktif** firma (supplier
 * çok-firmalı olabilir — switcher ile değişen aktif firma esas; çözülemezse güvenle boş).
 */
export interface SupplierMeDto {
  uuid: string;
  firstName: string;
  lastName: string;
  emailOrPhone: string;
  companyUuid: string | null;
  companyName: string | null;
}

/**
 * Ad-soyad düzenleme gövdesi — `PUT /me` + `PUT /supplier/me` (api-contract §18,
 * `UpdateProfileRequest`). Her iki alan **zorunlu** (boş/whitespace → `400 VALIDATION_FAILED`,
 * alan kodu `FIELD_EMPTY`); sunucu trim eder. E-posta/telefon/parola bu uçla **değişmez**.
 * Kimlik ve aktif firma daima token principal'dan okunur — istemci `userUuid`/`companyId`
 * göndermez (§18 scope izolasyonu).
 */
export interface UpdateProfileRequest {
  firstName: string;
  lastName: string;
}

/**
 * Parola değiştirme gövdesi — `POST /me/change-password` + `POST /supplier/me/change-password`
 * (api-contract §18, `ChangePasswordRequest`). İkisi de plaintext (boş → `400 VALIDATION_FAILED`);
 * sunucuda PBKDF2 ile işlenir. Mevcut yanlış → `400 WRONG_PASSWORD`; zayıf yeni (min 8 + harf +
 * rakam ihlali) → `400 PASSWORD_POLICY_ERROR` (parola değişmez). **Parola asla saklanmaz/loglanmaz.**
 */
export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

/**
 * `GET /me` — giriş yapan **kullanıcının kendi** profili (yalnız `user` principal;
 * supplier/kiosk → 403 FORBIDDEN; kimlik yok → 401). api-contract §18.1.
 */
export function getMe() {
  return api.get<MeDto>('me');
}

/**
 * `PUT /me` — giriş yapan **kullanıcının kendi** ad-soyadını günceller → `Result<MeDto>`
 * (güncellenmiş). Boş ad/soyad → `400 VALIDATION_FAILED`; yanlış principal → `403 FORBIDDEN`.
 * Kimlik token'dan; başka kullanıcı hedeflenemez. api-contract §18.1.
 */
export function updateMe(body: UpdateProfileRequest) {
  return api.put<MeDto>('me', { json: body });
}

/**
 * `POST /me/change-password` — giriş yapan **kullanıcının kendi** parolasını değiştirir →
 * `Result` (200, boş veri). Mevcut yanlış → `400 WRONG_PASSWORD`; zayıf yeni →
 * `400 PASSWORD_POLICY_ERROR`. Başarıda mevcut oturum yenileme bozulmaz. api-contract §18.1.
 */
export function changePassword(body: ChangePasswordRequest) {
  return api.post<Empty>('me/change-password', { json: body });
}

/**
 * `GET /supplier/me` — giriş yapan **taşeron personelinin kendi** profili + **aktif** firma
 * adı (yalnız `supplier` principal; user/kiosk → 403 FORBIDDEN; aktif firma yok →
 * 403 COMPANY_NOT_ACCESSIBLE; kimlik yok → 401). api-contract §18.2.
 */
export function getSupplierMe() {
  return api.get<SupplierMeDto>('supplier/me');
}

/**
 * `PUT /supplier/me` — giriş yapan **taşeron personelinin kendi** ad-soyadını günceller →
 * `Result<SupplierMeDto>` (güncellenmiş, aktif firma adı dahil). Boş ad/soyad →
 * `400 VALIDATION_FAILED`; yanlış principal → `403 FORBIDDEN`; aktif firma yok →
 * `403 COMPANY_NOT_ACCESSIBLE`. Kimlik/aktif firma token'dan. api-contract §18.2.
 */
export function updateSupplierMe(body: UpdateProfileRequest) {
  return api.put<SupplierMeDto>('supplier/me', { json: body });
}

/**
 * `POST /supplier/me/change-password` — giriş yapan **taşeron personelinin kendi** parolasını
 * değiştirir → `Result` (200, boş veri). Kurallar user ucuyla simetrik (`WRONG_PASSWORD` /
 * `PASSWORD_POLICY_ERROR`). Parola değişimi aktif firma seçimi gerektirmez (transit token'da
 * da çalışır). api-contract §18.2.
 */
export function changeSupplierPassword(body: ChangePasswordRequest) {
  return api.post<Empty>('supplier/me/change-password', { json: body });
}
